import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { endpoints, fmtUSD, fmtTime } from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import { ArrowLeft, Inbox, Bot, ShieldAlert, FileSignature, Receipt, Webhook, Bell, Wrench, BookOpen, CheckCircle2, XCircle, Sparkles, Cloud, RefreshCw, FastForward } from "lucide-react";
import { useBrand } from "@/context/BrandContext";
import { toast } from "sonner";

const STAGES = [
  { key: "apom", label: "Hand-off" },
  { key: "agent", label: "Agent" },
  { key: "gate", label: "Approval gate" },
  { key: "authorization", label: "Authorization" },
  { key: "cppo", label: "CPPO" },
];

const ICON = {
  received: Inbox, dispatched: Bot, tool_call: Wrench, playbook: BookOpen, approval_required: ShieldAlert, done: Sparkles,
  bypass: FastForward, error: XCircle, drafted: FileSignature, published: FileSignature, aws_result: Cloud, released: Receipt, consumed: CheckCircle2, notification: Bell, webhook: Webhook,
};
const TONE = {
  apom: "border-sky-500/50 text-sky-700", agent: "border-zinc-400 text-zinc-700", gate: "border-orange-500/60 text-orange-600",
  authorization: "border-amber-500/50 text-amber-700", cppo: "border-emerald-500/50 text-emerald-700",
};

function Card({ title, children, testId }) {
  return (
    <div className="border border-zinc-200 bg-zinc-50 p-4" data-testid={testId}>
      <div className="tx-overline mb-2">{title}</div>
      {children}
    </div>
  );
}
const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3 text-[12px] py-1 border-b border-zinc-200/70 last:border-b-0">
    <span className="text-zinc-500">{k}</span><span className="font-mono text-zinc-800 text-right truncate">{v ?? "—"}</span>
  </div>
);

export default function DealDetail() {
  const { dealId } = useParams();
  const brand = useBrand();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const refreshAws = async () => {
    setChecking(true);
    try {
      const r = await endpoints.ammpStatus(data.authorization.id);
      toast[r.status === "FAILED" ? "error" : "success"](`AWS change set: ${r.status || r.mode}`);
      setData(await endpoints.dealTimeline(dealId));
    } catch (e) { toast.error(e.response?.data?.detail || "AWS status check failed"); } finally { setChecking(false); }
  };

  useEffect(() => {
    const load = () => endpoints.dealTimeline(dealId).then(setData).catch(() => setError("Deal not found in this workspace"));
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [dealId]);

  if (error) return <div className="p-10 text-rose-600 text-[13px]" data-testid="deal-detail-error">{error}</div>;
  if (!data) return <div className="p-10 text-zinc-500 font-mono text-[12px]">loading timeline…</div>;
  const { deal, run, authorization: authz, offer, timeline } = data;
  const reached = new Set(timeline.map((t) => t.stage));
  const current = [...STAGES].reverse().find((s) => reached.has(s.key))?.key;

  return (
    <div className="max-w-[1400px] px-12 py-14" data-testid="page-deal-detail">
      <Link to="/handoff" data-testid="back-to-handoff" className="inline-flex items-center gap-1.5 text-[12px] text-zinc-500 hover:text-zinc-900 mb-4"><ArrowLeft className="w-3.5 h-3.5" /> {brand.handoff_label}</Link>
      <div className="flex items-end justify-between mb-8 gap-6">
        <div>
          <div className="tx-overline mb-2">{brand.source_app_name} · {deal.motion} · {deal.external_deal_id}</div>
          {deal.bypass && <div data-testid="deal-bypass-badge" className="inline-flex items-center gap-1.5 font-mono text-[10px] text-orange-700 border border-orange-300 bg-orange-50 px-2 py-0.5 mb-2"><FastForward className="w-3 h-3" /> Straight to private offer · benefit options bypassed</div>}
          <h1 className="font-heading font-black text-4xl mb-1">{deal.buyer_company}</h1>
          <div className="font-mono text-[12px] text-zinc-600">{deal.product} · {fmtUSD(deal.tcv_usd)} · {deal.term_months}mo · {deal.preferred_reseller || "no reseller pref"}</div>
          {deal.incentive && <div className="text-[12px] text-amber-700 mt-2" data-testid="deal-incentive">incentive: {deal.incentive}</div>}
        </div>
        <StateBadge state={deal.status === "received" ? "draft" : deal.status === "dispatched" ? "running" : deal.status} />
      </div>

      {/* Stage rail */}
      <div className="grid grid-cols-5 border border-zinc-200 mb-10" data-testid="stage-rail">
        {STAGES.map((s, i) => {
          const hit = reached.has(s.key);
          const isCurrent = s.key === current;
          return (
            <div key={s.key} data-testid={`stage-${s.key}`} className={`px-4 py-3 ${i < STAGES.length - 1 ? "border-r border-zinc-200" : ""} ${hit ? "bg-zinc-100" : ""}`}>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${hit ? (isCurrent ? "bg-orange-500 tx-pulse-dot" : "bg-emerald-500") : "bg-zinc-200"}`} />
                <span className={`tx-overline ${hit ? "text-zinc-800" : "text-zinc-400"}`}>{s.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-10">
        <ol className="relative border-l border-zinc-200 ml-2 space-y-5" data-testid="deal-timeline">
          {timeline.map((t, i) => {
            const Icon = ICON[t.kind] || Sparkles;
            return (
              <li key={i} data-testid={`timeline-${t.kind}`} className="pl-7 relative tx-fade-in" style={{ animationDelay: `${Math.min(i * 40, 600)}ms` }}>
                <span className={`absolute -left-[13px] top-0.5 w-6 h-6 bg-white border rounded-sm flex items-center justify-center ${TONE[t.stage]}`}>
                  <Icon className="w-3 h-3" strokeWidth={1.8} />
                </span>
                <div className="flex items-baseline justify-between gap-4">
                  <div className={`text-[13px] ${t.kind === "approval_required" ? "text-orange-700 font-medium" : "text-zinc-900"}`}>{t.title}</div>
                  <span className="font-mono text-[10px] text-zinc-500 shrink-0">{fmtTime(t.ts)}</span>
                </div>
                {t.detail && <div className="text-[12px] text-zinc-600 mt-0.5 break-words">{t.detail}</div>}
                {t.kind === "playbook" && t.data?.matched && (
                  <div className="mt-2 border border-zinc-200 bg-white p-2.5 text-[11px]" data-testid="timeline-playbook-card">
                    <div className="font-mono text-orange-600 mb-1">{Object.entries(t.data.recommendation || {}).map(([k, v]) => `${k}=${v ?? "—"}`).join(" · ")}</div>
                    <div className="text-zinc-600">{t.data.marketplace_benefit}</div>
                  </div>
                )}
                {t.kind === "aws_result" && t.data && (
                  <div className="mt-2 border border-zinc-200 bg-zinc-50 p-2.5 text-[11px] font-mono" data-testid="timeline-aws-result">
                    <div className={t.data.status === "SUCCEEDED" ? "text-emerald-700" : t.data.status === "FAILED" ? "text-rose-600" : "text-amber-700"}>status={t.data.status} · change_set={t.data.change_set_id}{t.data.failure_code ? ` · ${t.data.failure_code}` : ""}</div>
                    {(t.data.errors || []).map((e, j) => <div key={j} className="text-rose-600 mt-1">{e.ErrorCode}: {e.ErrorMessage}</div>)}
                    {(t.data.entity_ids || []).length > 0 && <div className="text-zinc-500 mt-1">entities: {t.data.entity_ids.join(", ")}</div>}
                  </div>
                )}
                {t.kind === "approval_required" && t.data?.risks?.length > 0 && (
                  <ul className="mt-1.5 space-y-0.5">{t.data.risks.map((r, j) => <li key={j} className="text-[11px] text-amber-700">⚠ {r}</li>)}</ul>
                )}
                {t.ref?.type === "run" && <Link to={`/agent?run=${t.ref.id}`} data-testid="timeline-open-run" className="inline-block mt-1 font-mono text-[10px] text-zinc-500 hover:text-orange-600">open live trace →</Link>}
              </li>
            );
          })}
        </ol>

        <div className="space-y-4">
          <Card title="Agent run" testId="card-run">
            {run ? (<>
              <Row k="run" v={run.id} /><Row k="status" v={run.status} /><Row k="started" v={fmtTime(run.created_at)} /><Row k="completed" v={fmtTime(run.completed_at)} />
              {run.playbook_match?.matched && <Row k="playbook" v={run.playbook_match.playbook_key} />}
              <Link to={`/agent?run=${run.id}`} className="inline-block mt-2 font-mono text-[11px] text-orange-600 hover:underline">Open in Agent Console →</Link>
            </>) : <div className="text-[12px] text-zinc-500">Not dispatched yet.</div>}
          </Card>
          <Card title="Selling Authorization" testId="card-authorization">
            {authz ? (<>
              <Row k="id" v={authz.id} /><Row k="status" v={authz.status} /><Row k="mode" v={authz.mode} /><Row k="pricing" v={authz.pricing_model} />
              <Row k="term" v={`${authz.duration_months}mo`} /><Row k="TCV" v={fmtUSD(authz.total_contract_value_usd)} /><Row k="RCMP" v={`${authz.rcmp_percent}%`} />
              <Row k="reseller" v={authz.reseller} /><Row k="AMMP" v={authz.ammp_authorization_id} /><Row k="submission" v={authz.ammp_submission_mode} />
              {authz.incentive_playbook && <Row k="playbook" v={authz.incentive_playbook} />}
              {authz.ammp_submission_mode === "live" && (
                <button onClick={refreshAws} disabled={checking} data-testid="refresh-aws-status" className="mt-2 h-7 px-2.5 border border-zinc-300 hover:border-orange-500/60 hover:text-orange-600 text-[11px] font-mono flex items-center gap-1.5 disabled:opacity-40">
                  <RefreshCw className={`w-3 h-3 ${checking ? "animate-spin" : ""}`} /> Refresh AWS change-set status
                </button>
              )}
              {authz.structure_rationale && <div className="text-[11px] text-zinc-600 mt-2 border-l-2 border-orange-500/50 pl-2" data-testid="structure-rationale">{authz.structure_rationale}</div>}
            </>) : <div className="text-[12px] text-zinc-500">Not drafted yet.</div>}
          </Card>
          <Card title="Private Offer (CPPO)" testId="card-offer">
            {offer ? (<>
              <Row k="id" v={offer.id} /><Row k="state" v={offer.state} /><Row k="released" v={fmtTime(offer.released_at)} /><Row k="markup" v={`${offer.isv_markup_percent}%`} />
              <Link to="/offers" className="inline-block mt-2 font-mono text-[11px] text-orange-600 hover:underline">View in Private Offers →</Link>
            </>) : <div className="text-[12px] text-zinc-500">Released after owner approval.</div>}
          </Card>
        </div>
      </div>
    </div>
  );
}
